/**
 * Feature detection for the WebMCP model-context API.
 *
 * ChatGPT's in-app browser exposes ONLY `document.modelContext`.
 * Chrome 152 (behind chrome://flags/#enable-webmcp-testing, i.e.
 * --enable-features=WebMCPTesting) ALSO exposes only `document.modelContext`
 * (`navigator.modelContext` is absent) and folds the automation surface into
 * it: `await document.modelContext.getTools()` -> RegisteredTool[] (inputSchema is a
 * JSON *string*), and `.executeTool(registeredTool, input)`.
 * `navigator.modelContextTesting` does not exist in Chrome 152; it is probed
 * anyway for older/newer builds.
 */

export interface ToolAnnotations {
  title?: string;
  readOnlyHint?: boolean;
  destructiveHint?: boolean;
  idempotentHint?: boolean;
  openWorldHint?: boolean;
  untrustedContentHint?: boolean;
  consequentialHint?: boolean;
}

export interface NativeToolDescriptor {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  annotations?: ToolAnnotations;
  execute: (input: unknown, opts?: { signal?: AbortSignal }) => unknown | Promise<unknown>;
}

/** Minimal shape of the platform object; everything optional so probes are safe. */
export interface ModelContextLike {
  registerTool?: (tool: NativeToolDescriptor, opts?: { signal?: AbortSignal }) => unknown;
  unregisterTool?: (name: string) => unknown;
  provideContext?: (ctx: { tools?: NativeToolDescriptor[] }) => unknown;
  clearContext?: () => unknown;
  getTools?: (opts?: unknown) => unknown;
  /**
   * Chrome 152 testing surface (same object as registerTool).
   * `tool` must be an item returned by getTools() (a RegisteredTool), NOT a name string.
   */
  executeTool?: (tool: unknown, input: unknown) => unknown;
  addEventListener?: (type: string, cb: (ev: Event) => void, opts?: AddEventListenerOptions) => void;
  removeEventListener?: (type: string, cb: (ev: Event) => void) => void;
}

let cached: ModelContextLike | null | undefined;

export function getModelContext(): ModelContextLike | null {
  if (cached !== undefined) return cached;
  const d = (document as any).modelContext;
  const n = (navigator as any).modelContext;
  cached = (d ?? n ?? null) as ModelContextLike | null;
  return cached;
}

export function hasWebMCP(): boolean {
  const mc = getModelContext();
  return !!mc && typeof mc.registerTool === 'function';
}

/** Where the API was found, for the status indicator. */
export function webmcpSource(): 'document' | 'navigator' | 'none' {
  if ((document as any).modelContext?.registerTool) return 'document';
  if ((navigator as any).modelContext?.registerTool) return 'navigator';
  return 'none';
}

/** Which automation surface exists (only with --enable-features=WebMCPTesting). */
export function testingSurface(): 'navigator.modelContextTesting' | 'document.modelContext' | 'none' {
  if ((navigator as any).modelContextTesting) return 'navigator.modelContextTesting';
  const mc = getModelContext();
  if (mc && typeof mc.executeTool === 'function' && typeof mc.getTools === 'function') return 'document.modelContext';
  return 'none';
}

export function hasTestingAPI(): boolean {
  return testingSurface() !== 'none';
}
